/**
 * Icon set — inline SVG, authored not imported.
 *
 * The editor has a first-class `svg` element type whose `svgContent` may use a
 * `{color}` template that `processSvgForRender` substitutes with the element's
 * `fill` (see components/Editor/svgUtils.js). Authoring icons that way means
 * every icon in a template stays a real, selectable, RE-COLOURABLE element in
 * the editor rather than a flattened picture — which is what "100% editable"
 * has to mean for the property-spec row and the contact bar.
 *
 * All icons share a 24×24 viewBox so a template can size them uniformly.
 */

/**
 * Every shape in a line icon carries an EXPLICIT `fill="none"`.
 *
 * `processSvgForRender` substitutes `{color}` into the markup and only then
 * tests `raw.includes('{color}')` to decide whether to run its tint fallback —
 * by which point the placeholder is gone, so the fallback always fires and
 * paints a fill onto every un-filled shape. That turns a stroked outline into a
 * solid blob. The tint explicitly skips anything already carrying `fill="none"`
 * (svgUtils.js "Safe-tint rules"), so declaring it per shape is what keeps
 * these icons line drawings — inheriting `fill="none"` from the root <svg> is
 * not enough, because the fallback writes the attribute onto the child.
 */
const unfilled = (body) => body.replace(/<(path|rect|circle|ellipse|polygon|polyline|line)\s/g, '<$1 fill="none" ');

const svg = (body, { stroke = true } = {}) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${stroke ? 'none' : '{color}'}"${
    stroke ? ' stroke="{color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"' : ''
  }>${stroke ? unfilled(body) : body}</svg>`;

export const ICONS = {
  /** Double-gable roof mark with a window — the Elite Realty brandmark. */
  roofMark: svg(
    '<path d="M2 11.2 8 6l6 5.2"/><path d="M9.5 11.2 15.5 6l6.5 5.2"/>' +
    '<rect x="9.6" y="13.4" width="4.8" height="4.6"/><path d="M12 13.4v4.6M9.6 15.7h4.8"/>',
  ),

  /** Bed — bedrooms. */
  bed: svg('<path d="M2 17v-5h20v5"/><path d="M2 17v2M22 17v2"/><path d="M4 12V8h7v4"/><path d="M13 12V8h7v4"/>'),

  /** Freestanding tub — bathrooms. */
  bath: svg('<path d="M3 12h18v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4v-3Z"/><path d="M6 19l-1 2M18 19l1 2"/><path d="M7 12V7a2 2 0 0 1 4 0"/>'),

  /** Corner arrows in a frame — floor area. */
  area: svg('<rect x="3" y="3" width="18" height="18" rx="1"/><path d="M8 8h3M8 8v3M8 8l3.5 3.5"/><path d="M16 16h-3M16 16v-3M16 16l-3.5-3.5"/>'),

  /** Garage door under a roof — car garage. */
  garage: svg('<path d="M2 10 12 4l10 6"/><path d="M4 10v10h16V10"/><path d="M7 20v-6h10v6"/><path d="M7 17h10"/>'),

  /** Check — feature list bullets. */
  check: svg('<path d="M4 12.5 9.5 18 20 6.5"/>'),

  /** Map pin — address. */
  pin: svg('<path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z"/><circle cx="12" cy="10" r="2.6"/>'),

  /** Handset — phone. */
  phone: svg('<path d="M6.5 3h3l1.5 4-2 1.5a12 12 0 0 0 5.5 5.5L16 12l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4 6.2 2 2 0 0 1 6.5 3Z"/>'),

  /** Envelope — email. */
  mail: svg('<rect x="2.5" y="5" width="19" height="14" rx="1.5"/><path d="m3 6.5 9 6.5 9-6.5"/>'),

  /** Globe — website. */
  globe: svg('<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18Z"/>'),

  /** Calendar — open-house date. */
  calendar: svg('<rect x="3" y="5" width="18" height="16" rx="1.5"/><path d="M3 10h18M8 3v4M16 3v4"/>'),

  /** Price tag. */
  tag: svg('<path d="M3 12.5V4a1 1 0 0 1 1-1h8.5L21 11.5 12.5 20 3 12.5Z"/><circle cx="7.5" cy="7.5" r="1.4"/>'),

  /** Dollar in a ring — sale price. */
  dollar: svg('<circle cx="12" cy="12" r="9"/><path d="M12 6.5v11M14.8 9.3c0-1.3-1.3-2-2.8-2s-2.8.6-2.8 1.9c0 3 5.6 1.5 5.6 4.6 0 1.4-1.3 2.1-2.8 2.1s-2.8-.7-2.8-2"/>'),

  /** Rotating-house glyph — virtual tour. */
  tour: svg('<path d="M5 10.5 12 5l7 5.5"/><path d="M7 10v7h10v-7"/><path d="M3.2 15.5A9 9 0 0 0 12 21a9 9 0 0 0 8.8-5.5"/><path d="m3.2 15.5 2.6.6M20.8 15.5l-2.6.6"/>'),

  /** Filled star — review row. */
  star: svg('<path d="m12 3 2.7 5.9 6.3.7-4.7 4.3 1.3 6.4L12 17.1 6.4 20.3l1.3-6.4L3 9.6l6.3-.7L12 3Z"/>', { stroke: false }),

  /** Opening quote — testimonial. */
  quote: svg('<path d="M9 6c-3 1.6-4.6 4.2-4.6 7.4V19H10v-6H7.2c0-2.4.8-4.2 2.6-5.2L9 6Zm10 0c-3 1.6-4.6 4.2-4.6 7.4V19H20v-6h-2.8c0-2.4.8-4.2 2.6-5.2L19 6Z"/>', { stroke: false }),
};

export const ICON_NAMES = Object.keys(ICONS);

export const getIcon = (name) => {
  const content = ICONS[name];
  if (!content) throw new Error(`templateLibrary: unknown icon "${name}"`);
  return content;
};
